import { z } from 'zod';
import { TimestampSchema } from '../common.js';
/**
 * A lifecycle or interaction event on a session — see docs/data-model.md §4.11.
 *
 * PK = TENANT#<tenantId>
 * SK = SESSION#<sessionId>#EVENT#<timestampISO>#<eventId>
 *
 * The SK ordering means begins_with queries over the session prefix return
 * events in chronological order without needing a separate sort key.
 *
 * The `type` enum is the extension point for new event categories. Payload
 * shape is intentionally loose (free-form record) so we don't have to cut a
 * shared-types release for every new event kind added by the agent-runtime.
 * Common shapes (non-binding):
 *   - `cta-click`  : { ctaId, url }
 *   - `language-change`: { fromLang, toLang, source }
 *   - `voice-end`  : { durationSec, reason }
 *   - `text-chat-end`: { durationSec, reason }
 *   - `communication-method-switch`: { from, to, source }
 *   - `error`      : { code, message }
 */
export const KnownSessionEventTypeSchema = z.enum([
    'view',
    'video-start',
    'video-complete',
    'voice-start',
    'voice-end',
    'text-chat-start',
    'text-chat-end',
    'communication-method-switch',
    'cta-click',
    'language-change',
    'error',
    'custom',
    // ---- Reporting events (phase-2 ingest) ----
    'turn-error',
    'interruption',
    'mic-denied',
    'connection-drop',
    'tool-call',
]);
export const SessionEventTypeSchema = z.union([
    KnownSessionEventTypeSchema,
    z.string().min(1).max(80),
]);
/**
 * Upper bound for a JSON-serialised event payload (4 KB). Enforced in the
 * append-events handler; reject at validation time so we never store a row
 * that would blow up DDB's 400 KB item limit when combined with the rest of
 * the event metadata.
 */
export const EVENT_PAYLOAD_MAX_BYTES = 4 * 1024;
export const SessionEventSchema = z.object({
    eventId: z.string().uuid(),
    sessionId: z.string().uuid(),
    tenantId: z.string().uuid(),
    type: SessionEventTypeSchema,
    timestamp: TimestampSchema,
    payload: z.record(z.unknown()),
});
//# sourceMappingURL=sessionEvent.js.map